import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, Clock, Zap, Hash, AlertTriangle } from "lucide-react";
import { useAgentMetrics } from "@/hooks/useAgentDetail";
import type { MetricsWindow } from "@/services/aiAgentsService";
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  CartesianGrid,
} from "recharts";

interface MetricsTabProps {
  agentId: number;
  timeWindow: MetricsWindow;
}

function formatBucket(iso: string, granularity: string): string {
  const d = new Date(iso);
  if (granularity === "hour") {
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function ComingSoonCard({ title, icon: Icon }: { title: string; icon: React.ElementType }) {
  return (
    <Card className="rounded-[15px] shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center h-[200px] text-muted-foreground">
          <div className="text-center">
            <div className="text-xs font-medium uppercase tracking-wider mb-1">Coming Soon</div>
            <p className="text-xs">Data not available for this provider</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MetricsTab({ agentId, timeWindow }: MetricsTabProps) {
  const { data, isLoading, isError } = useAgentMetrics(agentId, timeWindow);

  const chartData = useMemo(
    () =>
      data?.time_series.map((dp) => ({
        ...dp,
        label: formatBucket(dp.bucket, data.granularity),
      })) ?? [],
    [data]
  );

  const summary = data?.summary;
  const hasLatency = summary ? summary.avg_latency_ms > 0 : false;
  const hasCost = summary ? summary.estimated_cost > 0 : false;
  const hasTokens = summary ? summary.total_tokens > 0 : false;

  return (
    <div className="space-y-6 pt-6">
      {/* Error state */}
      {isError && (
        <Card className="rounded-[15px] shadow-none">
          <CardContent className="py-8 text-center text-destructive">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-60" />
            <p className="text-sm">Failed to load performance metrics.</p>
          </CardContent>
        </Card>
      )}

      {/* Summary strip */}
      {!isError && <Card className="rounded-[15px] shadow-none">
        <CardContent className="py-4">
          {isLoading ? (
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 divide-x divide-border">
              <div className="pl-4 first:pl-0">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Requests</div>
                <div className="text-2xl font-bold mt-0.5">
                  {formatNumber(summary?.total_requests ?? 0)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {summary?.error_rate.toFixed(1) ?? 0}% error rate
                </div>
              </div>
              <div className="pl-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Tokens</div>
                <div className="text-2xl font-bold mt-0.5">
                  {formatNumber(summary?.total_tokens ?? 0)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {summary?.tokens_per_request.toFixed(0) ?? 0} avg/req
                </div>
              </div>
              <div className="pl-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Avg Latency</div>
                <div className="text-2xl font-bold mt-0.5">
                  {hasLatency ? `${summary?.avg_latency_ms.toFixed(0)}ms` : "--"}
                </div>
                {summary?.p95_latency_ms != null && (
                  <div className="text-xs text-muted-foreground">P95: {summary.p95_latency_ms}ms</div>
                )}
                {!hasLatency && (
                  <div className="text-xs text-muted-foreground">Not available</div>
                )}
              </div>
              <div className="pl-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Est. Cost</div>
                <div className="text-2xl font-bold mt-0.5">
                  {hasCost ? `$${summary?.estimated_cost.toFixed(4)}` : "--"}
                </div>
                {!hasCost && (
                  <div className="text-xs text-muted-foreground">Not available</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>}

      {/* Empty state */}
      {!isError && !isLoading && chartData.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <Activity className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No performance data available for this time window.</p>
        </div>
      )}

      {/* Charts */}
      {!isError && isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="rounded-[15px] shadow-none">
              <CardContent className="pt-6">
                <Skeleton className="h-[220px] w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isError && !isLoading && chartData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Request Volume Chart — always shown */}
          <Card className="rounded-[15px] shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                Request Volume
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-2 flex items-center justify-end gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-muted-foreground">Requests</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-muted-foreground">Errors</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} barGap={0}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    tickLine={false}
                    axisLine={{ stroke: "#e5e7eb" }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                    }}
                  />
                  <Bar dataKey="request_count" name="Requests" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="error_count" name="Errors" fill="#ef4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Token Usage Chart — shown when tokens exist */}
          {hasTokens ? (
            <Card className="rounded-[15px] shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5 text-muted-foreground" />
                  Token Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-2 flex items-center justify-end gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Input</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-muted-foreground">Output</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatNumber}
                      width={45}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                      }}
                    />
                    <Bar
                      dataKey="total_input_tokens"
                      name="Input"
                      stackId="tokens"
                      fill="#3b82f6"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="total_output_tokens"
                      name="Output"
                      stackId="tokens"
                      fill="#10b981"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            <ComingSoonCard title="Token Usage" icon={Zap} />
          )}

          {/* Latency Chart — conditional */}
          {hasLatency ? (
            <Card className="rounded-[15px] shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  Latency (ms)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={false}
                      width={45}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                      }}
                      formatter={(value) => [`${Number(value).toFixed(1)}ms`, "Avg Latency"]}
                    />
                    <Bar dataKey="avg_latency_ms" name="Avg Latency" fill="#6366f1" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            <ComingSoonCard title="Latency" icon={Clock} />
          )}

          {/* Cost Chart — conditional */}
          {hasCost ? (
            <Card className="rounded-[15px] shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Estimated Cost ($)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={false}
                      width={45}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                      }}
                      formatter={(value) => [`$${Number(value).toFixed(6)}`, "Cost"]}
                    />
                    <Bar dataKey="estimated_cost" name="Cost" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            <ComingSoonCard title="Estimated Cost" icon={Activity} />
          )}
        </div>
      )}
    </div>
  );
}
